import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Message from './Message';
import Spinner from '../Spinner';
import useApi from '../api';
import { MessageType } from '../types';
import ChatInput from './ChatInput';
import { conLog } from './utils/conLog';
import useAnalytics from '../analytics/liveAnalytics';
import { v4 as uuidv4 } from 'uuid';
import { hexToRgb } from './utils/agentUtils';
import {
    useFormFields,
    useInactivityTimer,
    useAutoScroll,
    useAgentConfig,
    useIpAddress,
    useOnlineListener,
    useActionResultsListener,
    useStartTaskListener,
    useInitialMessageSender,
    useCleanupOnUnmount,
    useHandBackChatEffect
} from './agentHooks';

interface newMessage {
    role: string;
    content: string;
}

interface AgentProps {
    agentId?: string;
    closeModal?: any;
    sidebar?: boolean;
    setSidebar?: any;
    embedConfig?: any;
    agentLinks?: any;
    lastConvId?: any;
    setConvId?: any;
    lastMessages?: MessageType[];
    agentStarters?: any;
    existLeadId?: string;
    visitorId?: string;
    welcomeMessage: string;
    queryParams?: any;
    draftAgentId?: string;
    parentUrl?: string;
    currentPageOnly?: boolean;
    currentPage?: string;
    messageInput?: newMessage;
    setMessageInput?: any;
    lang?: string;
    isMobile?: boolean;
}

const AgentChat = ({
    agentId,
    closeModal,
    sidebar,
    setSidebar,
    embedConfig,
    agentLinks,
    agentStarters,
    lastConvId,
    setConvId,
    lastMessages,
    existLeadId,
    visitorId,
    welcomeMessage,
    queryParams,
    draftAgentId,
    parentUrl,
    currentPageOnly,
    currentPage,
    messageInput,
    setMessageInput,
    isMobile,
    lang
}: AgentProps) => {
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<MessageType[]>(lastMessages ? lastMessages : []);
    const [conversationId, setConversationId] = useState(lastConvId);
    const [assistantResponse, setAssistantResponse] = useState('');
    const [leadId, setLeadId] = useState<string | undefined>(existLeadId);
    const [noLead, setNoLead] = useState(false);
    const [links, setLinks] = useState<any>(agentLinks);
    const [starters, setStarters] = useState<any>(agentStarters);
    const [followUps, setFollowUps] = useState<any>([]);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [initMessageSent, setInitMessageSent] = useState(messages.filter((message) => message.role === 'user').length > 0);
    const [results, setResults] = useState<any>([]);
    const [gettingResponse, setGettingResponse] = useState(false);
    const [gettingFullResponse, setGettingFullResponse] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);
    const { get, post, postStream, connectWebSocket } = useApi();

    const [firstName, setFirstName] = useState(queryParams && queryParams['first_name'] ? queryParams['first_name'] : '');
    const [lastName, setLastName] = useState(queryParams && queryParams['last_name'] ? queryParams['last_name'] : '');
    const [email, setEmail] = useState(queryParams && queryParams['aimdoc_email'] ? queryParams['aimdoc_email'] : '');
    const [attributes, setAttributes] = useState<Record<string, string>>({});
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [company, setCompany] = useState(queryParams && queryParams['company'] ? queryParams['company'] : '');
    const [jobTitle, setJobTitle] = useState(queryParams && queryParams['job_title'] ? queryParams['job_title'] : '');
    const [showFollowUps, setShowFollowUps] = useState(false);
    const [agentName, setAgentName] = useState<string | undefined>(undefined);
    const [showAgentEnteredMessage, setShowAgentEnteredMessage] = useState(false);
    const [showAgentLeftMessage, setShowAgentLeftMessage] = useState(false);
    const [showConnectedMessage, setShowConnectedMessage] = useState(false);
    const [handBackChat, setHandBackChat] = useState(false);
    const [takeOverChat, setTakeOverChat] = useState(false);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const [updateFormFields, setUpdateFormFields] = useState(false);
    const [sessionInitialized, setSessionInitialized] = useState(false);
    const [currentPageContext, setCurrentPageContext] = useState<any>(undefined);
    const [isEscalated, setIsEscalated] = useState(false);
    const [gdprAgreed, setGdprAgreed] = useState(false);
    const shouldReconnectRef = useRef(true);

    const { captureEvent } = useAnalytics();

    const { config, logo } = useAgentConfig(embedConfig, agentId, draftAgentId);
    const ipAddress = useIpAddress();
    const [formFields, setFormFields] = useFormFields(conversationId, updateFormFields);

    const socketRef = useRef<WebSocket | null>(null);
    const chatRef = React.useRef<HTMLDivElement | null>(null);

    const messagesRef = React.useRef(messages);
    messagesRef.current = messages;

    const assistantResponseRef = React.useRef(assistantResponse);
    assistantResponseRef.current = assistantResponse;

    const conversationIdRef = React.useRef(conversationId);
    conversationIdRef.current = conversationId;

    const leadIdRef = React.useRef(leadId);
    leadIdRef.current = leadId;

    const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

    const abortControllerRef = useRef(new AbortController());

    const [pendingMessages] = useState(new Set<string>());

    const [executingFunction, setExecutingFunction] = useState(false);
    const [functionName, setFunctionName] = useState<string | null>(null);

    const resetInactivityTimeout = useInactivityTimer(15 * 60 * 1000, () => {
        shouldReconnectRef.current = false;
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
            console.warn('WebSocket connection closed due to inactivity');
        }
    });

    const scrollToBottom = () => {
        if (chatRef.current) {
            chatRef.current.scrollTo({
                top: chatRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    useAutoScroll(shouldScroll, chatRef, () => setShouldScroll(false));

    const [queuedMessage, setQueuedMessage] = useState<newMessage | undefined>(undefined);

    const sendMessage = async (input: string, type?: string, leadData?: any) => {
        const messageId = `${Date.now()}-${input.slice(0, 10)}`;

        if (pendingMessages.has(messageId)) {
            console.warn('Message already being processed, preventing duplicate');
            return;
        }

        try {
            pendingMessages.add(messageId);
            resetInactivityTimeout();

            const date = new Date().toISOString();

            setMessages((prevMessages) => [...prevMessages, {
                id: messageId,
                role: type || 'user',
                content: input,
                created_at: date
            }]);

            if (!takeOverChat) {
                setGettingResponse(true);
                setGettingFullResponse(true);
            }
            setAssistantResponse('');
            setFollowUps([]);

            if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
                try {
                    await initializeWebSocket();
                    await new Promise<void>((resolve, reject) => {
                        const checkConnection = setInterval(() => {
                            if (socketRef.current?.readyState === WebSocket.OPEN) {
                                clearInterval(checkConnection);
                                resolve();
                            } else if (socketRef.current?.readyState === WebSocket.CLOSED) {
                                clearInterval(checkConnection);
                                reject(new Error('WebSocket connection failed'));
                            }
                        }, 100);

                        setTimeout(() => {
                            clearInterval(checkConnection);
                            reject(new Error('WebSocket connection timeout'));
                        }, 5000);
                    });
                } catch (wsError) {
                    console.error('WebSocket connection error:', wsError);
                    throw wsError;
                }
            }

            let pageContext = null;
            if (config?.enable_page_awareness) {
                pageContext = await new Promise((resolve) => {
                    const handlePageContextMessage = (event: MessageEvent) => {
                        if (event.data.type === 'aimdocPageContext') {
                            window.removeEventListener('message', handlePageContextMessage);
                            resolve(event.data.data);
                        }
                    };

                    window.addEventListener('message', handlePageContextMessage);
                    window.parent.postMessage({ type: 'aimdocGetPageContext' }, '*');

                    setTimeout(() => {
                        window.removeEventListener('message', handlePageContextMessage);
                        resolve(null);
                    }, 2000);
                });
            }

            const clickEvents = localStorage.getItem('aimdocEvents') || '[]';
            const clickEventsArray = JSON.parse(clickEvents);

            const messagePayload: any = {
                id: messageId,
                type: 'message',
                role: type || 'user',
                content: input,
                created_at: date,
                parent_window_url: parentUrl,
                welcome_message: { role: 'assistant', content: welcomeMessage, created_at: date },
                visitor_ip: ipAddress,
                current_page_only: currentPageOnly,
                current_page: currentPage,
                visitor_id: visitorId,
                lead_id: leadIdRef.current,
                lead_data: leadData,
                click_events: clickEventsArray,
                query_params: queryParams
            };

            conLog(messagePayload);

            if (pageContext) {
                messagePayload['current_page_context'] = pageContext;
            }

            if (conversationId && !takeOverChat) {
                socketRef.current?.send(JSON.stringify({ 'type': 'ai_responding', 'responding': true }));
            }

            socketRef.current?.send(JSON.stringify(messagePayload));
            setShouldScroll(true);

            localStorage.removeItem('aimdocEvents');
            window.parent.postMessage('clearClickEvents', '*');

        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => [...prev, {
                role: "assistant",
                content: "I apologize, but I'm having trouble connecting. Please try again in a moment.",
                created_at: new Date().toISOString()
            }]);
            setGettingFullResponse(false);
            setGettingResponse(false);
        } finally {
            pendingMessages.delete(messageId);
        }
    };

    const sendEscalation = async (reason: string) => {
        post(`/sessions/${conversationId}/send-escalation/`, {
            reason: reason
        }).then((response) => {
            console.log(response);
        });
    }

    const getFollowUps = async (conversationId: string) => {
        post(`/ai-follow-ups/${conversationId}`, {})
            .then(({ data }) => {
                setFollowUps(JSON.parse(data).follow_ups);
                setTimeout(() => {
                    setShouldScroll(true);
                }, 500);
            })
            .catch(error => console.error('Error getting follow ups', error));
    }

    const handleClickEvent = (clickData: any) => {
        const clickEventId = uuidv4();
        clickData.id = clickEventId;

        if (conversationId) {
            post(`/sessions/${conversationId}/process-click-event/`, clickData).then((response) => {
                if (response.data.message === "Click event processed successfully" ||
                    response.data.message === "Click event already processed") {
                    localStorage.removeItem('aimdocEvents');
                    window.parent.postMessage('clearClickEvents', '*');
                } else {
                    console.warn('Unexpected response from server:', response.data);
                }
            }).catch((error) => {
                console.error('Error processing click event:', error);
            });
        } else {
            console.warn('ConversationId or visitorId not available, storing event locally');
            window.parent.postMessage({ type: 'storeClickEvent', data: clickData }, '*');
            let aimdocEvents = JSON.parse(localStorage.getItem('aimdocEvents') || '[]');
            aimdocEvents.push(clickData);
            localStorage.setItem('aimdocEvents', JSON.stringify(aimdocEvents));
        }
    };

    const trackWidgetClick = (element: string, elementText: string, elementType: string = 'interaction') => {
        const clickData = {
            x: 0,
            y: 0,
            timestamp: new Date().toISOString(),
            element: element,
            element_name: `aimdoc_widget_${elementType}`,
            element_text: elementText,
            parent_window_url: parentUrl,
            source: 'widget'
        };
        handleClickEvent(clickData);
    };

    const selectFollowUp = (followUp: string) => {
        trackWidgetClick('BUTTON', followUp, 'follow_up');
        sendMessage(followUp, 'user');
        setFollowUps([]);
        captureEvent('used_follow_up', {
            agent_id: agentId,
            message: followUp,
            ip_address: ipAddress,
        });
    }

    const checkIfMobile = () => {
        return window.innerWidth < 1025;
    };

    const newSession = () => {
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        setConversationId('');
        setTakeOverChat(false);
        setShowConnectedMessage(false);
        setMessages([]);
        setFormSubmitted(false);
        setInitMessageSent(false);
        setFollowUps([]);
        setAssistantResponse('');
        setGettingResponse(false);
        setGettingFullResponse(false);
        setShowAgentEnteredMessage(false);
        setShowAgentLeftMessage(false);
        setAgentName(undefined);
        setHandBackChat(false);

        if (setConvId) {
            setConvId('');
        }

        shouldReconnectRef.current = true;
    };

    useEffect(() => {
        if (email && formSubmitted) {
            if (parentUrl) {
                attributes['aimdoc_parent_url'] = parentUrl;
            }
            const leadData = {
                agent_id: draftAgentId ? draftAgentId : agentId,
                visitor_id: visitorId,
                first_name: firstName,
                last_name: lastName,
                email: email,
                company: company,
                job_title: jobTitle,
                attributes: attributes,
                visitor_ip: ipAddress,
                conversation_id: conversationId,
            };

            if (socketRef.current) {
                sendMessage(`The user has submitted the form. Their email is (${email}).`, 'system', leadData);
            } else {
                sendMessage(`The user has submitted the form. Their email is (${email}).`, 'system', leadData);
            }
        }

        if (queryParams && queryParams['aimdoc_email'] && email && !initMessageSent && !gettingFullResponse) {
            sendMessage(`A lead named ${firstName} has landed on your website. Please greet them by their name!`, 'system');
        }

        if (!initMessageSent && !conversationId && (leadId || noLead)) {
            setInitMessageSent(true);
        }

    }, [firstName, lastName, email, company, jobTitle, ipAddress]);

    useEffect(() => {
        if (followUps.length > 0) {
            setShowFollowUps(true);
        } else {
            setShowFollowUps(false);
        }
    }, [followUps]);

    const setMessage = (message: MessageType) => {
        setMessages((prevMessages) => {
            const messageExists = prevMessages.some(msg => msg.id === message.id);
            if (messageExists) {
                return prevMessages;
            } else {
                return [...prevMessages, message];
            }
        });
    }


    const initializeWebSocket = async () => {
        if (socketRef.current?.readyState === WebSocket.CONNECTING) {
            await new Promise<void>((resolve) => {
                const checkConnection = setInterval(() => {
                    if (socketRef.current?.readyState !== WebSocket.CONNECTING) {
                        clearInterval(checkConnection);
                        resolve();
                    }
                }, 100);
            });
            return;
        }

        try {
            const endpoint = conversationId ?
                `/ws/${conversationId}?role=user&token=` :
                `/ws/new?role=user&token=&agent_id=${draftAgentId || agentId}`;

            const ws = await connectWebSocket(endpoint);

            ws.onclose = (event) => {
                console.warn('WebSocket connection closed');
                setGettingResponse(false);
                setGettingFullResponse(false);
                setAssistantResponse('');

                if (shouldReconnectRef.current) {
                    const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
                    setTimeout(() => {
                        setReconnectAttempts((prev) => prev + 1);
                        initializeWebSocket();
                    }, delay);
                }
            };

            return new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('WebSocket connection timeout'));
                }, 5000);

                ws.onopen = () => {
                    clearTimeout(timeout);
                    socketRef.current = ws;
                    setReconnectAttempts(0);
                    if (conversationId) {
                        ws.send(JSON.stringify({ type: 'status', status: 'active' }));
                        ws.send(JSON.stringify({ type: 'get_execution_state' }));
                    }
                    resolve();

                };

                ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        if (data.type === 'conversation_created') {
                            setConversationId(data.conversation_id);
                            ws.send(JSON.stringify({ type: 'status', status: 'active' }));
                            if (setConvId) {
                                setConvId(data.conversation_id);
                            }
                            return;
                        }

                        switch (data.type) {
                            case 'message':
                                if (data.role === 'assistant') {
                                    const newMessage = {
                                        id: data.id,
                                        role: data.role,
                                        content: data.content,
                                        created_at: data.created_at || new Date().toISOString(),
                                        agent_name: data.agent_name
                                    };
                                    setMessage(newMessage);
                                    setIsAgentTyping(false);
                                }
                                break;

                            case 'assistant':
                                if (data.content) {
                                    setGettingResponse(false);
                                    setAssistantResponse(prev => {
                                        const newResponse = prev + data.content;
                                        return newResponse;
                                    });
                                }

                                if (data.final_message) {
                                    setGettingFullResponse(false);
                                    if (data.conversation_id) {
                                        setConversationId(data.conversation_id);
                                        if (setConvId) {
                                            setConvId(data.conversation_id);
                                        }
                                    }
                                    if (data.lead_id) {
                                        setLeadId(data.lead_id);
                                    }

                                    const finalMessage = {
                                        role: 'assistant',
                                        content: data.final_message,
                                        created_at: new Date().toISOString()
                                    };
                                    setMessages(prev => [...prev, finalMessage]);
                                    setAssistantResponse('');

                                    if (config.enable_follow_ups && data.conversation_id) {
                                        getFollowUps(data.conversation_id);
                                    }
                                    socketRef.current?.send(JSON.stringify({ 'type': 'ai_responding', 'responding': false }));
                                }
                                break;

                            case 'take_over_chat':
                                setAgentName(data.agent_name);
                                setShowAgentEnteredMessage(true);
                                setTakeOverChat(true);
                                setFollowUps([]);

                                const takeOverMessage = {
                                    id: data.id,
                                    type: 'take_over_chat',
                                    role: 'system',
                                    content: `${data.agent_name ? data.agent_name : 'Agent'} has connected.`,
                                    created_at: new Date().toISOString(),
                                    agent_name: data.agent_name
                                };
                                setMessage(takeOverMessage);

                                setTimeout(() => {
                                    setShowConnectedMessage(true);
                                    scrollToBottom();
                                }, 1500); break;

                            case 'hand_back_chat':
                                setTakeOverChat(false);
                                setHandBackChat(true);
                                setShowAgentLeftMessage(true);
                                setShowConnectedMessage(false);
                                // setGettingResponse(true);
                                // setGettingFullResponse(true);
                                const message = {
                                    id: data.id,
                                    type: data.type,
                                    role: 'system',
                                    content: `${data.agent_name ? data.agent_name : 'Agent'} has left the chat.`,
                                    created_at: new Date().toISOString(),
                                    agent_name: data.agent_name
                                };
                                setMessage(message);
                                scrollToBottom();
                                setTimeout(() => {
                                    setShowAgentLeftMessage(false);
                                }, 4000);

                                setAgentName(undefined);
                                break;

                            case 'agent_closed':
                                setTakeOverChat(false);
                                setHandBackChat(true);
                                setShowAgentLeftMessage(true);
                                setShowConnectedMessage(false);
                                // setGettingResponse(true);
                                // setGettingFullResponse(true);
                                const agentClosedMessage = {
                                    id: data.id,
                                    type: data.type,
                                    role: 'system',
                                    content: `${data.agent_name ? data.agent_name : 'Agent'} has left the chat.`,
                                    created_at: new Date().toISOString(),
                                    agent_name: data.agent_name
                                };
                                setMessage(agentClosedMessage);
                                scrollToBottom();
                                setTimeout(() => {
                                    setShowAgentLeftMessage(false);
                                }, 4000);

                                setAgentName(undefined);
                                break;

                            case 'agent_typing':
                                setIsAgentTyping(data.is_typing);
                                break;

                            case 'document':
                                setResults(data.content);
                                break;

                            case 'function_call':
                                if (data.status === 'started') {
                                    setExecutingFunction(true);
                                    setFunctionName("Updating Information");
                                    setTimeout(() => {
                                        setExecutingFunction(false);
                                        setFunctionName(null);
                                    }, 1000);
                                }
                                break;

                        }

                        scrollToBottom();
                        setShouldScroll(true);

                    } catch (error) {
                        console.error('Error parsing WebSocket message:', error);
                    }
                };
            });
        } catch (error) {
            console.error('Failed to establish WebSocket connection:', error);
            throw error;
        }
    };

    useEffect(() => {
        if (!conversationId || socketRef.current) return;

        shouldReconnectRef.current = true;

        if (conversationId && !socketRef.current) {
            initializeWebSocket();
        }
        return () => {
            shouldReconnectRef.current = false;
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, [conversationId]);

    useEffect(() => {
        const initializeAgent = async () => {
            if (!conversationId) {
                setSessionInitialized(true);
                return;
            }
            let userId = localStorage.getItem('aimdocUserId');
            if (!userId) {
                return;
            }

            try {
                const { data } = await post(`/latest-session/`, {
                    visitor_id: userId,
                    agent_id: agentId,
                });

                if (data.is_human_agent) {
                    setMessages(data.messages);
                    setShowAgentEnteredMessage(true);
                    setTakeOverChat(true);
                    setFollowUps([]);

                    setTimeout(() => {
                        setShowConnectedMessage(true);
                        scrollToBottom()
                    }, 1000);
                }

                setSessionInitialized(true);

            } catch (error) {
                // Handle error if needed
            }
        };

        initializeAgent();

    }, []);

    useInitialMessageSender(
        sessionInitialized && (!config?.gdpr_enabled || gdprAgreed),
        messageInput,
        sendMessage,
        setMessageInput,
        conversationId,
        socketRef
    );

    useEffect(() => {
        if (sessionInitialized && messageInput && config?.gdpr_enabled && !gdprAgreed) {
            setQueuedMessage(messageInput);
            setMessageInput(undefined);
        }
    }, [sessionInitialized, messageInput, config?.gdpr_enabled, gdprAgreed]);

    useCleanupOnUnmount(socketRef, abortControllerRef, setReconnectAttempts, shouldReconnectRef);
    useHandBackChatEffect(handBackChat, setAgentName, setShowConnectedMessage, setHandBackChat);

    useEffect(() => {
        if (isMobile) {
            const originalStyle = window.getComputedStyle(document.body);
            const originalOverflow = originalStyle.overflow;
            
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            
            if (chatRef.current) {
                chatRef.current.style.touchAction = 'pan-y';
            }
            
            return () => {
                document.body.style.overflow = originalOverflow;
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.height = '';
            };
        }
    }, []);

    const memoizedMessages = useMemo(() => {
        const messagesArray = messages
            .sort((a, b) => {
                const dateA = new Date(a.created_at.endsWith('Z') ? a.created_at : `${a.created_at}Z`);
                const dateB = new Date(b.created_at.endsWith('Z') ? b.created_at : `${b.created_at}Z`);
                return dateA.getTime() - dateB.getTime();
            });

        return messagesArray.map((message, index) => {
            const isLastAssistantMessage = message.role === 'assistant' &&
                index === messagesArray.length - 1;

            return config && message.role != 'system' ? (
                <Message
                    key={index}
                    role={message.role}
                    content={message.content}
                    created_at={message.created_at}
                    leadEmail={email}
                    scrollToBottom={scrollToBottom}
                    sidebar={sidebar}
                    agentName={message.agent_name}
                    conversationId={conversationId}
                    sendMessage={sendMessage}
                    setLeadId={setLeadId}
                    formFields={formFields}
                    parentUrl={parentUrl}
                    isLastAssistantMessage={isLastAssistantMessage}
                    onFormFieldUpdate={(fieldName: string, value: string) => {
                        setFormFields(prev => prev.map(field =>
                            field.name === fieldName
                                ? { ...field, value }
                                : field
                        ));
                    }}
                    trackWidgetClick={trackWidgetClick}
                    config={{
                        color: config.color,
                        end_color: config.color,
                        darker_color: config.darker_color,
                        lighter_color: config.lighter_color,
                        mixed_color: config.mixed_color,
                        scheme: config.scheme,
                        agent_message_icon_type: config.agent_message_icon_type,
                        agent_message_icon: config.agent_message_icon,
                        message_icon_url: config.message_icon_url
                    }}
                />
            ) : (
                <div className="">
                    {message.type === 'take_over_chat' && (
                        <div className="flex flex-row justify-center items-center text-sm font-medium text-green-500 mt-2 mb-2">
                            <div className="flex mr-2 bg-green-50 items-center px-2 py-1 rounded-lg border border-green-200">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4">
                                    <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                </svg>
                                <div className="ml-2">
                                    {`${message.agent_name ? message.agent_name : 'Agent'} has taken over the chat.`}
                                </div>
                            </div>
                        </div>
                    )}
                    {message.type === 'hand_back_chat' && (
                        <div className="flex flex-row justify-center items-center text-sm font-medium text-amber-500 mt-2 mb-2">
                            <div className="flex mr-2 bg-amber-50 items-center px-2 py-1 rounded-lg border border-amber-200">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4">
                                    <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                </svg>

                                <div className="ml-2">
                                    {`${message.agent_name ? message.agent_name : 'Agent'} has left the chat.`}
                                </div>
                            </div>
                        </div>
                    )}
                    {message.type === 'agent_closed' && (
                        <div className="flex flex-row justify-center items-center text-sm font-medium text-amber-500 mt-2 mb-2">
                            <div className="flex mr-2 bg-amber-50 items-center px-2 py-1 rounded-lg border border-amber-200">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4">
                                    <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                </svg>

                                <div className="ml-2">
                                    {`${message.agent_name ? message.agent_name : 'Agent'} has left the chat.`}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )
        });
    }, [messages, config, email, conversationId, formFields]);

    useOnlineListener(() => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
            initializeWebSocket();
        }
    });

    useStartTaskListener();

    useEffect(() => {
        if (conversationId) {
            setGdprAgreed(true);
        }
    }, [conversationId]);

    return (
        messages && config && (
            <div className={`relative flex flex-col ${agentId ? 'h-full' : 'h-screen'} w-full`}>
                <div className={`flex justify-between items-center w-full`} style={config.scheme === 'Solid' ? { backgroundColor: config.color } : { backgroundImage: `linear-gradient(to bottom right, ${config.lighter_color}, ${config.darker_color})` }}>
                    <div className={`items-center transition-all duration-200 h-14`}>
                        <img src={logo} className="mt-4 mb-4 ml-4 h-6"></img>
                    </div>
                    {agentId && (
                        <div className="flex flex-row">
                            <div onClick={() => { newSession(); trackWidgetClick('BUTTON', 'New Session', 'navigation'); }} className={"flex items-center justify-end pr-6 hover:cursor-pointer"}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-6 h-6 stroke-white transform hover:rotate-180 duration-200`}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                            </div>
                            <div onClick={() => { closeModal(); trackWidgetClick('BUTTON', 'Close Chat', 'navigation'); }} className="flex items-center justify-end pr-6 hover:cursor-pointer">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 stroke-white transform hover:rotate-180 duration-200">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-row h-full bg-white">
                    <div className="flex flex-col w-full h-full">
                        {links && links.length > 0 && (
                            <div className="bg-opacity-100 z-30 w-full">
                                <div className={`flex flex-row p-2 ${links.length < 2 ? 'justify-center' : ''} overflow-x-auto scrollbar-transparent mx-4 space-x-2`}>
                                    {links.length > 0 &&
                                        links.map((link: any, index: number) => (
                                            <a
                                                key={index}
                                                href={link.url}
                                                target="_blank"
                                                onClick={() => trackWidgetClick('LINK', link.name, 'resource_link')}
                                                className={`bg-white border rounded-xl py-1 px-3 flex flex-col flex-shrink-0 justify-between leading-normal hover:cursor-pointer transition duration-200 hover:bg-white hover:shadow-sm`}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                }}
                                            >
                                                <div className="flex flex-row items-center justify-center">
                                                    <div className="flex flex-row justify-start mr-1">
                                                        <h1
                                                            className={`text-black/70 text-sm font-semibold transition duration-200 hover:text-black hover:cursor-pointer`}
                                                            style={{
                                                                color: `rgba(${hexToRgb(config.color)}, 1)`
                                                            }}
                                                        >
                                                            {link.name}
                                                        </h1>
                                                    </div>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor"
                                                        className="w-4 h-4 stroke-slate-500"
                                                        style={{
                                                            stroke: `rgba(${hexToRgb(config.color)}, 1)`
                                                        }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                    </svg>
                                                    {!sidebar && !checkIfMobile() && (
                                                        <div>
                                                            <p className="text-slate-500 text-sm">{link.description}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </a>
                                        ))}
                                </div>
                            </div>
                        )}
                        <div className={`transition-all duration-300 ease-in-out ${showConnectedMessage ? 'opacity-100 mb-2' : 'opacity-0 mb-0 h-0'} overflow-hidden`}>
                            <div className={`flex flex-row justify-center items-center ${links && links.length === 0 ? 'mb-2 mt-2' : 'mb-2'} animate-pulse`}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4 fill-green-500">
                                    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                                </svg>

                                <div className="ml-2 flex text-xs font-medium text-green-500">
                                    <p>Connected with {agentName} (Human)</p>
                                </div>
                            </div>
                        </div>
                        {((links && links.length > 0) || takeOverChat) && (
                            <div className="border-t border-slate-100 mx-6 shadow-sm"></div>
                        )}
                        <div ref={chatRef} className={`flex flex-col w-full items-end overflow-y-auto scrollbar-hidden h-full scroll-smooth overscroll-contain -webkit-overflow-scrolling-touch ${messages.length === 0 ? '' : 'mb-16'}`}>
                            <div className={`space-y-4 p-5 w-full h-full justify-center max-w-6xl items-start`}>
                                {!firstName && (
                                    <Message
                                        key={welcomeMessage}
                                        role={'assistant'}
                                        content={welcomeMessage}
                                        leadEmail={email}
                                        scrollToBottom={scrollToBottom}
                                        conversationId={conversationId}
                                        setLeadId={setLeadId}
                                        parentUrl={parentUrl}
                                        trackWidgetClick={trackWidgetClick}
                                        config={{
                                            color: config.color,
                                            end_color: config.color,
                                            darker_color: config.darker_color,
                                            lighter_color: config.lighter_color,
                                            mixed_color: config.mixed_color,
                                            scheme: config.scheme,
                                            agent_message_icon_type: config.agent_message_icon_type,
                                            agent_message_icon: config.agent_message_icon,
                                            message_icon_url: config.message_icon_url,
                                        }}
                                    />
                                )}
                                {messages && messages.length === 0 &&(
                                    <div
                                        className="items-center text-xs text-slate-400 mx-16 [&_a]:text-blue-400"
                                        dangerouslySetInnerHTML={{ __html: config?.translations?.policy_text || '' }}
                                    />
                                )}
                                {memoizedMessages}
                                {(assistantResponseRef.current || gettingResponse || isAgentTyping || executingFunction) &&
                                    <Message
                                        role="assistant"
                                        content={assistantResponseRef.current}
                                        leadEmail={email}
                                        scrollToBottom={scrollToBottom}
                                        gettingResponse={gettingResponse}
                                        gettingFullResponse={gettingFullResponse}
                                        isAgentTyping={isAgentTyping}
                                        agentName={agentName}
                                        conversationId={conversationId}
                                        sendMessage={sendMessage}
                                        setLeadId={setLeadId}
                                        parentUrl={parentUrl}
                                        executingFunction={executingFunction}
                                        functionName={functionName || undefined}
                                        trackWidgetClick={trackWidgetClick}
                                        config={{
                                            color: config.color,
                                            end_color: config.color,
                                            darker_color: config.darker_color,
                                            lighter_color: config.lighter_color,
                                            mixed_color: config.mixed_color,
                                            scheme: config.scheme,
                                            agent_message_icon_type: config.agent_message_icon_type,
                                            agent_message_icon: config.agent_message_icon,
                                            message_icon_url: config.message_icon_url,
                                        }}
                                    />}
                                {config && config.escalation_enabled && config.escalation_buttons.length > 0 && !gettingFullResponse && conversationId !== '' && conversationId && !isEscalated && !takeOverChat && (
                                    <div className="flex flex-row gap-2 px-2 justify-center">
                                        {config.escalation_buttons.map((button: any) => (
                                            <div
                                                key={button.id}
                                                className={`p-1.5 rounded-lg ${followUps && followUps.length > 0 ? '' : 'mb-5'} text-xs hover:cursor-pointer font-medium hover:scale-105 transition duration-150`}
                                                onClick={() => {
                                                    trackWidgetClick('BUTTON', button.caption, 'escalation');
                                                    sendMessage(button.instructions, 'system', undefined);
                                                    sendEscalation(button.caption);
                                                    setIsEscalated(true);
                                                }}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.04)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <p className="font-bold">{button.caption}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {config && config.escalation_enabled && config.escalation_buttons.length > 0 && followUps && followUps.length > 0 && !isEscalated && !takeOverChat && (
                                    <div className="flex items-center justify-center w-full mb-2 px-4">
                                        <div className="border-t border-slate-200 w-full"></div>
                                    </div>
                                )}
                                {config && config.gdpr_enabled && !gdprAgreed && (
                                    <div className="flex flex-col items-center w-full mb-4 px-4 mt-4">
                                        <div className="flex flex-row gap-2 justify-center w-full">
                                            <button
                                                className={`p-2 rounded-lg text-xs hover:cursor-pointer font-medium hover:scale-105 transition duration-150`}
                                                onClick={() => {
                                                    setGdprAgreed(true);
                                                    trackWidgetClick('BUTTON', 'GDPR Consent - Yes', 'gdpr');
                                                    if (queuedMessage) {
                                                        sendMessage(queuedMessage.content, queuedMessage.role);
                                                        setQueuedMessage(undefined);
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.04)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                I Agree
                                            </button>
                                            <button
                                                className="p-2 rounded-lg text-xs hover:cursor-pointer font-medium hover:scale-105 transition duration-150 bg-red-50 border border-red-100 text-red-500"
                                                onClick={() => {
                                                    trackWidgetClick('BUTTON', 'GDPR Consent - No', 'gdpr');
                                                    if (closeModal) {
                                                        closeModal();
                                                    }
                                                }}
                                            >
                                                I Decline
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {followUps && followUps.length > 0 && (
                                    <div className={`flex flex-col gap-2 pb-2 ml-auto mr-1 transition-opacity duration-[400ms] ease-in-out ${showFollowUps ? 'opacity-100' : 'opacity-0'}`}>
                                        {followUps.map((followUp: any, index: number) => (
                                            <div
                                                key={index}
                                                className={`p-2 rounded-2xl text-xs hover:cursor-pointer font-medium hover:scale-105 transition duration-150 ml-auto`}
                                                onClick={() => selectFollowUp(followUp)}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.03)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <p>{followUp}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {loading && <Spinner />}
                            </div>
                            <div className="flex w-full mt-4">
                                {starters && starters.length > 0 && messages.length === 0 && (!config?.gdpr_enabled || gdprAgreed) && (
                                    <div className="flex flex-col space-y-2 items-center w-full justify-center mx-16 mb-4">
                                        {starters.map((starter: any, index: number) => (
                                            <div
                                                key={index}
                                                className="p-2 rounded-2xl w-fit text-xs hover:cursor-pointer font-medium hover:scale-105 duration-150"
                                                onClick={() => {
                                                    trackWidgetClick('BUTTON', starter.value, 'starter');
                                                    sendMessage(starter.value);
                                                }}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.02)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <p>{starter.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {(!config?.gdpr_enabled || gdprAgreed) && (
                            <ChatInput
                                sendMessage={sendMessage}
                                config={config}
                                gettingFullResponse={gettingFullResponse}
                                setEmail={setEmail}
                                setFirstName={setFirstName}
                                setLastName={setLastName}
                                setCompany={setCompany}
                                setAttributes={setAttributes}
                                setFormSubmitted={setFormSubmitted}
                                leadId={leadId}
                                fullScreen={false}
                                socketRef={socketRef}
                                conversationId={conversationId}
                                formFields={formFields}
                                trackWidgetClick={trackWidgetClick}
                                isMobile={isMobile}
                            />
                        )}
                    </div>
                </div>
            </div>
        )

    );

}

export default AgentChat;